import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["syncButton", "statusBadge"]
  static values = {
    integrationId: String,
    instanceId: String
  }

  connect() {
    console.log("Linear sync controller connected", {
      integrationId: this.integrationIdValue,
      instanceId: this.instanceIdValue
    })

    // Subscribe to the Linear sync channel
    this.subscription = consumer.subscriptions.create(
      {
        channel: "LinearSyncChannel",
        integration_id: this.integrationIdValue
      },
      {
        connected: () => {
          console.log("Connected to LinearSyncChannel")
        },
        disconnected: () => {
          console.log("Disconnected from LinearSyncChannel")
        },
        received: (data) => {
          console.log("Received sync update:", data)
          this.handleSyncUpdate(data)
        }
      }
    )
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  handleSyncUpdate(data) {
    // Handle different sync statuses
    if (data.status === 'completed' || data.status === 'incremental_complete') {
      this.showSuccessNotification(data)
      this.enableSyncButton()

      // Reload the page to show updated issues after a short delay
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } else if (data.status === 'failed') {
      this.showErrorNotification(data)
      this.enableSyncButton()
    } else if (data.status === 'syncing') {
      this.disableSyncButton()
    }
  }

  showSuccessNotification(data) {
    let message = `Sync completed! ${data.issues_synced || 0} issues processed`

    const details = []
    if (data.issues_created) details.push(`${data.issues_created} new`)
    if (data.issues_updated) details.push(`${data.issues_updated} updated`)
    if (data.issues_deleted) details.push(`${data.issues_deleted} deleted`)

    if (details.length > 0) {
      message += ` (${details.join(', ')})`
    }

    this.showToast(message, 'success')
  }

  showErrorNotification(data) {
    const message = data.error || 'Sync failed. Please try again.'
    this.showToast(message, 'error')
  }

  showToast(message, type = 'info') {
    // Create a toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} shadow-lg fixed top-20 right-4 z-50 max-w-md`
    toast.innerHTML = `
      <div>
        ${this.getIcon(type)}
        <span>${message}</span>
      </div>
    `

    document.body.appendChild(toast)

    // Animate in
    setTimeout(() => {
      toast.style.opacity = '1'
    }, 10)

    // Remove after 5 seconds
    setTimeout(() => {
      toast.style.opacity = '0'
      setTimeout(() => {
        toast.remove()
      }, 300)
    }, 5000)
  }

  getIcon(type) {
    switch(type) {
      case 'success':
        return '<svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
      case 'error':
        return '<svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
      default:
        return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current flex-shrink-0 w-6 h-6"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>'
    }
  }

  disableSyncButton() {
    if (this.hasSyncButtonTarget) {
      this.syncButtonTarget.disabled = true
      this.syncButtonTarget.classList.add('loading')
      this.syncButtonTarget.innerHTML = `
        <span class="loading loading-spinner"></span>
        Syncing...
      `
    }
  }

  enableSyncButton() {
    if (this.hasSyncButtonTarget) {
      this.syncButtonTarget.disabled = false
      this.syncButtonTarget.classList.remove('loading')
      this.syncButtonTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="w-5 h-5 stroke-current">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        Sync Now
      `
    }
  }

  // Called when clicking the sync button
  startSync(event) {
    console.log("Starting sync...")
    this.disableSyncButton()

    // Show starting notification
    this.showToast('Starting sync...', 'info')
  }
}